<script setup>
import { ref, computed } from "vue";
import { Inertia } from '@inertiajs/inertia';
import { Head } from '@inertiajs/inertia-vue3';
import BreezeAuthenticatedLayout from '@/Layouts/Authenticated.vue';
import PremiumPageHeader from '@/Components/PremiumPageHeader.vue';
import TableFilter from "@/Components/TableFilter.vue";
import CardBoxComponentEmpty from '@/Components/CardBoxComponentEmpty.vue';
import Buttons from "@/Components/Buttons.vue";
import Dropdown from "@/Components/Dropdown.vue";
import DropdownLink from "@/Components/DropdownLink.vue";
import { Icon } from "@iconify/vue";

const props = defineProps({
    users: {
        type: [Array, Object],
        default: () => []
    },
    datalist: {
        type: [Array, Object],
        default: () => []
    },
    roles: {
        type: [Array, Object],
        default: () => []
    },
    userParam: [Array, Object]
});

const rutaPrefijo = ref('user');
const modulo = ref('usuarios');

// Opciones de mapeo local para select en columnas
const mapOptionsLocal = (obj) => {
    if (!obj) return undefined;
    if (Object.keys(obj).length === 0) return undefined;
    if (Array.isArray(obj)) {
        if (obj.length > 0 && typeof obj[0] !== 'object') {
            return obj.map((value, index) => ({ value: index, label: value }));
        }
        return obj.map(o => ({ value: isNaN(o.value) ? o.value : Number(o.value), label: o.label }));
    }
    return Object.entries(obj).map(([key, value]) => ({
        value: isNaN(key) ? key : Number(key),
        label: value
    }));
};

const usersData = computed(() => {
    return props.users && props.users.length ? props.users : (props.datalist || []);
});

const rolTypeOptions = [
    { value: 'admin', label: 'Admin' },
    { value: 'maestro', label: 'Maestro' },
    { value: 'estudiante', label: 'Estudiante' },
    { value: 'apoderado', label: 'Apoderado' },
    { value: 'coordinador', label: 'Coordinador' },
    { value: 'secretaria', label: 'Secretaria' },
    { value: 'diseño', label: 'Diseño' },
];

const columns = computed(() => {
    return [
        { key: 'actions', label: 'Acciones', align: 'center', width: '90px' },
        { key: 'id', label: 'ID', align: 'center', width: '70px' },
        { key: 'name', label: 'Nombre Completo', check: true },
        { key: 'user', label: 'Usuario', check: true },
        { key: 'email', label: 'Correo Electrónico' },
        {
            key: 'rol',
            label: 'Rol',
            filterType: 'select',
            filterOptions: props.roles ? props.roles.map(r => ({ value: r.label, label: r.label })) : []
        },
        {
            key: 'rol_type',
            label: 'Interfaz',
            filterType: 'select',
            filterOptions: rolTypeOptions
        },
        {
            key: 'created_at',
            label: 'Creación',
            filterType: 'datetime',
            align: 'center'
        },
        {
            key: 'status',
            label: 'Estado',
            filterType: 'select',
            isBoolean: true,
            booleanLabels: { true: 'Activo', false: 'Inactivo' },
            filterColor: [
                { value: 0, color: 'red' },
                { value: 1, color: 'green' }
            ],
            align: 'center'
        }
    ];
});

// Acción de cambiar estado Activo / Inactivo
const toggleStatus = (item) => {
    const isActivar = item.status == 0;
    const actionText = isActivar ? 'activar' : 'desactivar';
    const confirmMsg = `¿Está seguro de ${actionText} al usuario "${item.name}"?`;

    if (confirm(confirmMsg)) {
        const routeName = isActivar ? 'user-activar' : 'user-desactivar';
        Inertia.put(route(routeName), { id: item.id }, {
            preserveScroll: true
        });
    }
};
</script>

<template>
    <Head title="Usuarios" />

    <BreezeAuthenticatedLayout>
        <template #header>
            <PremiumPageHeader
                title="Usuarios"
                subtitle="Gestión y control de usuarios y accesos al sistema"
                icon="tabler:users"
                color="indigo"
                :actionsOnBottomOnMobile="false"
            >
                <template #actions>
                    <Buttons
                        label="Nuevo Usuario"
                        icon="f7:plus-app-fill"
                        size="vuexy"
                        color="blue"
                        :href="route('user-create')"
                        :hideLabelOnMobile="true"
                    />
                </template>
            </PremiumPageHeader>
        </template>

        <div class="py-2">
            <div class="card-premium p-6">
                <!-- Info banner -->
                <div class="mb-5 flex items-center gap-3 p-4 bg-indigo-50/60 dark:bg-indigo-950/20 border border-indigo-100/80 dark:border-indigo-900/50 rounded-xl text-sm text-indigo-800 dark:text-indigo-300">
                    <Icon icon="tabler:shield-lock" class="w-5 h-5 shrink-0 text-indigo-500 dark:text-indigo-400" />
                    <span>Control de accesos y roles de usuarios registrados en el sistema. Utilice los filtros para segmentar por rol o estado.</span>
                </div>

                <!-- Tabla con TableFilter -->
                <div v-if="usersData.length" class="bg-white dark:bg-slate-800 rounded-lg">
                    <div class="w-full relative">
                        <TableFilter
                            :data="usersData"
                            :columns="columns"
                            title="Usuarios"
                            :ruta="rutaPrefijo"
                            :modulo="modulo"
                            :userParam="userParam"
                            top-scrollbar
                            manage-columns
                            exporteable
                        >
                            <template #cell-actions="{ item }">
                                <div class="flex items-center justify-center">
                                    <Dropdown align="Right" width="48">
                                        <template #trigger>
                                            <Buttons size="iconSm" color="light" title="Opciones">
                                                <i class="fa-solid fa-ellipsis-vertical text-slate-500 dark:text-slate-400"></i>
                                            </Buttons>
                                        </template>
                                        <template #content>
                                            <div class="px-3 py-1.5 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider border-b border-slate-100 dark:border-slate-700">
                                                Acciones
                                            </div>

                                            <!-- Editar / Ver Usuario -->
                                            <DropdownLink :href="route('user-edit', { id: item.id })" class="flex items-center gap-2">
                                                <i class="fa-light fa-pen-to-square text-indigo-600 dark:text-indigo-400"></i>
                                                <span class="dark:text-slate-200">Editar Usuario</span>
                                            </DropdownLink>

                                            <!-- Activar / Desactivar -->
                                            <button
                                                type="button"
                                                @click="toggleStatus(item)"
                                                class="w-full text-left px-4 py-2 text-sm leading-5 flex items-center gap-2 transition duration-150 ease-in-out hover:bg-gray-100 dark:hover:bg-slate-700 focus:outline-none"
                                                :class="item.status == 1 ? 'text-rose-600 hover:text-rose-700' : 'text-emerald-600 hover:text-emerald-700'"
                                            >
                                                <i :class="item.status == 1 ? 'fa-light fa-ban text-rose-500' : 'fa-light fa-circle-check text-emerald-500'"></i>
                                                <span>{{ item.status == 1 ? 'Desactivar' : 'Activar' }}</span>
                                            </button>
                                        </template>
                                    </Dropdown>
                                </div>
                            </template>
                        </TableFilter>
                    </div>
                </div>

                <div v-else>
                    <CardBoxComponentEmpty />
                </div>
            </div>
        </div>
    </BreezeAuthenticatedLayout>
</template>
